import React, { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from "../../interceptor/axiosInstance";
import { formatTime } from '../Utils/UtilityService.js';
import NotificationService from '../Utils/NotificationService.js';

const ScanFile = () => {
  const [attachmentList, setAttachmentList] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);  // State for confirmation
  const [fileToDelete, setFileToDelete] = useState(null);  // File to delete
  const fileInputRef = useRef(null);
  const [isMaxLength, setIsMaxLength] = useState(false);
  const [isDifferentFormat, setIsDifferentFormat] = useState(false);
  const [file, setFile] = useState([]);
  const [maxUploads, setMaxUploads] = useState(null);

  // Function to open the file input dialog
  const handleIconClick = () => {
    setShowPopup(true);
  };

  // Handle file upload and update the file list
  const handleFileChange = async (event) => {
    setIsMaxLength(false);
    setIsDifferentFormat(false);

    if (!event.target.files) return;
    const selectedFiles = Array.from(event.target.files);

    await selectedFiles.forEach((file) => {
      if (file.size > 20 * 1024 * 1024) {
        setIsMaxLength(true);
        return;
      }
      if (!/\.(pdf)$/i.test(file.name)) {
        setIsDifferentFormat(true);
        return;
      }
    });
    setFile(selectedFiles);
  };

  const onCLickUpload = async () => {
    if (!isMaxLength && !isDifferentFormat) {
      setIsMaxLength(false);
      setIsDifferentFormat(false);

      const validFiles = [];
      const formDataToSubmit = new FormData();

      file.forEach((file) => {
        if (file.size <= 20 * 1024 * 1024 && /\.(pdf)$/i.test(file.name)) {
          validFiles.push(file);
          formDataToSubmit.append("attachments", file);
        }
      });

      if (validFiles.length > 0) {
        try {
          const response = await axiosInstance.post("/org/upload-file", formDataToSubmit, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          if (response.data && response.data.success) {
            NotificationService.success(response.data.message);
            fetchOrgAttachments();
            setShowPopup(false);
            setFile([]);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          } else {
            NotificationService.error(response.data.message || "File upload failed. Please try again.");
          }
        } catch (error) {
          if (error.response) {
            const errorMessage = error.response.data?.error || "An error occurred while uploading.";
            NotificationService.error(errorMessage);
          } else {
            console.error("Upload error:", error);
            alert("Network error. Please try again later.");
          }
        }
      }
    }
  };

  // Fetch the list of attachments
  const fetchOrgAttachments = async () => {
    try {
      const response = await axiosInstance.get(`/org/org-attachments`);
      setAttachmentList(response.data);
    } catch (error) {
      console.error('Error fetching organization attachments:', error);
    }
  };

  // Delete a file
  const handleDelete = async () => {
    if (fileToDelete) {
      const payload = { fileId: fileToDelete.id };
      try {
        const res = await axiosInstance.post(`/org/delete-file`, payload);
        console.log(`Deleted successfully`);
        // Update the file list after deletion
        setAttachmentList((prevList) => prevList.filter((item) => item.id !== fileToDelete.id));
        setShowConfirmDelete(false);  // Close confirmation after deletion
        setFileToDelete(null);  // Clear the file to delete
        NotificationService.success(res.data.message);
      } catch (error) {
        console.error(`Error deleting file`, error);
        NotificationService.error(`Error deleting file`);
      }
    }
  };

  // Show confirmation popup
  const confirmDelete = (file) => {
    setFileToDelete(file);
    setShowConfirmDelete(true);
  };

  // Close the confirmation popup
  const cancelDelete = () => {
    setShowConfirmDelete(false);
    setFileToDelete(null);
  };

  const getAccess = async () => {
    try {
      const response = await axiosInstance.get(`/org/check-access`);
      const maxAgentSetting = response.data.find(
        (setting) => setting.setting_key === "max_uploads"
      );
      if (maxAgentSetting) {
        setMaxUploads(parseInt(maxAgentSetting.setting_value, 10));
      }
    } catch (err) {
      console.error("Error fetching access settings", err);
    }
  };

  useEffect(() => {
    fetchOrgAttachments();
    getAccess();
  }, []);

  const isAddDisabled = maxUploads !== null && attachmentList.length >= maxUploads;


  return (
    <div>
      <div className="agent-container hero-section text-center mb-4 p-4" style={styles.heroSection}>
        <h2 className='heading-large'>Train Your Assistant with Files</h2>
        <p className='conv-content'>
          Upload files to train the assistant with your data. Your assistant will analyze these files and provide responses based on the information they contain.
        </p>
        <button className={isAddDisabled ? "btn btn-secondary-color" : "btn btn-color"} onClick={handleIconClick} disabled={isAddDisabled}>
          <FontAwesomeIcon icon={faUpload} size="lg" /> Upload Files
        </button>
        {isAddDisabled && (
          <div> <span style={{ color: "red", marginRight: "10px" }} className='conv-content'>
            Upload limit reached for your organization. You can only upload {maxUploads} files. Need more? Contact support for assistance.
          </span>
          </div>
        )}
      </div>

      {showPopup && (
        <div style={styles.popup}>
          <div style={styles.popupContent}>
            <div style={{ textAlign: 'end' }}>
              <button
                style={styles.closeButton}
                onClick={() => {
                  setFile([]);
                  setIsMaxLength(false);
                  setIsDifferentFormat(false);
                  setShowPopup(false)
                }}
                aria-label="Close"

              >
                &times;
              </button>
            </div>

            <h3 className='heading-large'>Upload File</h3>
            <p className='conv-content'>Choose the file you want your chat bot to use for answers.</p>
            <input
              type="file"
              // multiple
              onChange={handleFileChange}
              ref={fileInputRef}
              style={styles.fileInput}
              className='mb-2'
            />
            {isMaxLength && isDifferentFormat && (<div style={styles.validationMessage}>File must be smaller than 20 MB and of type .pdf</div>)}
            {isMaxLength && (<div style={styles.validationMessage}>File must be smaller than 20 MB</div>)}
            {isDifferentFormat && (<div style={styles.validationMessage}>File must be type of .pdf</div>)}
            <p className='details-font-value mt-3'>
              <ul>
                <li>Supported formats: PDF</li>
                <li>Maximum file size: 20MB</li>
                <li>PDFs with tables are not yet supported.</li>
              </ul>
            </p>

            <div style={{ textAlign: 'end' }}>
              <button
                className="btn btn-primary"
                onClick={onCLickUpload}
                style={styles.uploadButton}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
     
      {showConfirmDelete && (
        <div style={styles.popup}>
          <div style={styles.deletePopupContent}>
            <h3 style={styles.popupTitle}>Confirm Deletion</h3>
            <p style={styles.popupDescription}>
              Are you sure you want to delete the file <strong>{fileToDelete?.file_name}</strong>?
            </p>
            <div style={{ textAlign: 'end' }}>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                style={styles.deleteButton}
              >
                Yes, Delete
              </button>
              <button
                className="btn btn-secondary"
                onClick={cancelDelete}
                style={styles.cancelButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="agent-container">
        <div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr className="tbl-header-color conv-user">
                <th style={{ ...styles.headerCell, width: "25px" }}>No.</th>
                <th style={styles.headerCell}>File Name</th>
                <th style={styles.headerCell}>Date</th>
                <th style={{ ...styles.headerCell, width: "25px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {attachmentList && attachmentList.length > 0 ? (
                attachmentList.map((attachment, index) => (
                  <tr key={attachment.id} style={styles.row} className='conv-content'>
                    <td style={styles.cell}>{index + 1}</td>
                    <td style={styles.cell}>{attachment.file_name}</td>
                    <td style={styles.cell}>{formatTime(attachment.created_at)}</td>
                    <td style={styles.cell}>
                      <FontAwesomeIcon
                        icon={faTrashAlt}
                        style={{ cursor: 'pointer', color: "#ff0000" }}
                        onClick={() => confirmDelete(attachment)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={styles.noRecordCell} colSpan="4">
                    No files available!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const styles = {
  heroSection: {
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    padding: "20px",
  },
  heroTitle: {
    fontWeight: "bold",
    color: "#333",
  },
  heroText: {
    color: "#555",
    fontSize: "1rem",
    marginBottom: "1rem",
  },
  headerCell: {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
    fontWeight: "bold",
  },
  cell: {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
  },
  row: {
    backgroundColor: "#fff",
    ':hover': { backgroundColor: '#f9f9f9' },
  },
  noRecordCell: {
    padding: "10px",
    textAlign: "left",
    color: "#888",
    border: "1px solid #ddd",
  },

  closeButton: {
    top: '10px',
    right: '15px',
    background: 'none',
    border: 'none',
    fontSize: '30px',
    color: '#333',
  },

  popupContent: {
    backgroundColor: 'white',
    padding: '0px 20px 20px 20px',
    borderRadius: '5px'
  },

  deletePopupContent: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '5px'
  },

  popup: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },

  validationMessage: {
    fontSize: '14px',
    color: 'red'
  },

  deleteButton: {
    marginRight: '10px',
  },
  cancelButton: {
    marginLeft: '10px',
  }
};

export default ScanFile;